import { t, type Locale } from '@sd/i18n';
import type { Hex } from '@sd/sim';
import type { Note } from './events';
import { clock, escapeHtml } from './format';
import { icon } from './icons';

/**
 * Notifications, as in Civilization VI (#56, docs/design/ui-style.md): a
 * stack of icons at the map's right edge, the newest opened into words for
 * a few seconds; hover shows the words, a click flies the camera to the
 * place and dismisses the note. The dashed button on top opens the log of
 * everything (L). Crisis warnings and their projects live in the banner.
 */
const STACK = 6;
const OPEN_MS = 7000;

export class Notes {
  private readonly stack: HTMLElement;
  private readonly log: HTMLElement;
  private readonly all: Note[] = [];
  private shown: (Note & { readonly at: number })[] = [];
  private last = '';

  constructor(
    parent: HTMLElement,
    private readonly locale: Locale,
    private readonly flyTo: (hex: Hex) => void,
  ) {
    this.stack = document.createElement('aside');
    this.stack.className = 'sd-notes';
    this.stack.setAttribute('aria-label', t(locale, 'ui.notes.title'));
    this.log = document.createElement('div');
    this.log.className = 'sd-overlay';
    this.log.hidden = true;
    parent.append(this.stack, this.log);

    this.stack.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!button) return;
      if ('log' in button.dataset) return this.openLog(true);
      const index = Number(button.dataset.index);
      const note = this.shown[index];
      if (!note) return;
      if (note.hex) this.flyTo(note.hex);
      this.shown.splice(index, 1);
      this.render(performance.now());
    });
    this.log.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target === this.log || target.closest('[data-close]')) this.openLog(false);
    });
  }

  add(notes: readonly Note[], now: number) {
    for (const note of notes) {
      this.all.push(note);
      if (!note.bannerOnly) this.shown.unshift({ ...note, at: now });
    }
    this.shown = this.shown.slice(0, STACK);
    this.render(now);
  }

  /** Keeps the stack below the crisis banner and closes old notes into icons. */
  frame(top: number, now: number) {
    this.stack.style.top = `${Math.max(72, top + 12)}px`;
    this.render(now);
  }

  handleKey(event: KeyboardEvent): boolean {
    if (event.code === 'KeyL' && !(event.target instanceof HTMLInputElement)) {
      this.openLog(this.log.hidden === true);
      return true;
    }
    if (event.code === 'Escape' && !this.log.hidden) {
      this.openLog(false);
      return true;
    }
    return false;
  }

  private openLog(open: boolean) {
    if (open) {
      const items = [...this.all]
        .reverse()
        .map(
          (n) =>
            `<li data-kind="${n.kind}"><time>${clock(n.tick)}</time>${icon(n.icon, 16)}<span>${escapeHtml(n.text)}</span></li>`,
        )
        .join('');
      this.log.innerHTML = `
        <section class="sd-panel sd-panel--strong sd-dialog sd-log" role="dialog" aria-modal="true" aria-labelledby="log-title">
          <h2 id="log-title">${t(this.locale, 'ui.notes.title')}</h2>
          ${items ? `<ol>${items}</ol>` : `<p class="sd-muted">${t(this.locale, 'ui.notes.empty')}</p>`}
          <button class="sd-btn" type="button" data-close>${t(this.locale, 'ui.notes.close')} <kbd>Esc</kbd></button>
        </section>`;
    }
    this.log.hidden = !open;
  }

  private render(now: number) {
    const html =
      `<button class="sd-note sd-note--log" type="button" data-log title="${t(this.locale, 'ui.notes.log')}">${icon('log')}</button>` +
      this.shown
        .map((n, i) => {
          const open = now - n.at < OPEN_MS;
          return `<button class="sd-note sd-note--${n.kind}" type="button" data-index="${i}" title="${escapeHtml(`${clock(n.tick)} · ${n.text}`)}">${icon(n.icon)}${
            open
              ? `<span class="sd-note__text"><time>${clock(n.tick)}</time>${escapeHtml(n.text)}</span>`
              : ''
          }</button>`;
        })
        .join('');
    if (html === this.last) return;
    this.last = html;
    this.stack.innerHTML = html;
  }

  dispose() {
    this.stack.remove();
    this.log.remove();
  }
}
